'use client';

import type {
  ReputationOverviewDto,
  ReputationSummaryDto,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  healthAttentionSurfaceClass,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { ArrowRight } from 'lucide-react';

import {
  AiSummaryCard,
  type AiSummaryCardLabels,
} from '@/core/components/reputation/ai-summary-card';
import { StarRating } from '@/core/components/reputation/reputation-bits';
import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupLabel,
  SettingsGroupRow,
} from '@/core/components/settings/settings-group';
import { useIsMobile } from '@/core/hooks/use-mobile';
import type { LocationConnectionStatus } from '@/core/lib/review-channel-setup';

import { formatLocationReviewSummary } from './location-health';

export type LocationReviewInsightsLabels = {
  /** Fallback card title when KI summary is not the hero (listing / empty). */
  title: string;
  /** ★ {rating} · {count} reviews */
  summary: string;
  summaryAria: string;
  reviewsCount: string;
  emptySynced: string;
  listingOnly: string;
  /** Honest empty when reviews exist but KI summary is unavailable. */
  noSummary: string;
  openReputation: string;
  ctaConnectSync: string;
  aiSummary: AiSummaryCardLabels;
};

function RatingMetaLine({
  averageRating,
  summaryAria,
  summarySr,
  countLabel,
}: {
  averageRating: number;
  summaryAria: string;
  summarySr: string;
  countLabel: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1" aria-label={summaryAria}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xl font-semibold tabular-nums tracking-tight text-foreground">
          {averageRating.toFixed(1)}
        </span>
        <StarRating rating={averageRating} />
        <span className="text-[13.5px] leading-relaxed text-muted-foreground tabular-nums">
          <span className="sr-only">{summarySr}</span>
          <span aria-hidden>· {countLabel}</span>
        </span>
      </div>
    </div>
  );
}

/**
 * Location Overview reputation strip — KI summary first when reviews exist.
 * Charts stay on the Reputation surface; Overview stays readable Status Board.
 */
export function LocationReviewInsights({
  connectionStatus,
  overview,
  isLoading,
  summary,
  summaryLoading,
  summaryError,
  locale,
  labels,
  onOpenReputation,
  onOpenInbox,
  onConnectSync,
}: {
  connectionStatus: LocationConnectionStatus;
  overview: ReputationOverviewDto | undefined;
  isLoading: boolean;
  summary: ReputationSummaryDto | undefined;
  summaryLoading: boolean;
  summaryError: boolean;
  locale: string;
  labels: LocationReviewInsightsLabels;
  onOpenReputation: () => void;
  onOpenInbox: () => void;
  onConnectSync: () => void;
}) {
  const isMobile = useIsMobile();

  if (connectionStatus !== 'linked' && connectionStatus !== 'listing') {
    return null;
  }

  if (connectionStatus === 'listing') {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <SettingsGroupLabel>{labels.title}</SettingsGroupLabel>
        <SettingsGroup className={healthAttentionSurfaceClass}>
          <SettingsGroupRow>
            <Button
              variant="outline"
              className={isMobile ? 'w-full' : 'w-full sm:w-auto'}
              onClick={onConnectSync}
            >
              {labels.ctaConnectSync}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </SettingsGroupRow>
        </SettingsGroup>
        <SettingsGroupFooter>{labels.listingOnly}</SettingsGroupFooter>
      </div>
    );
  }

  if (isLoading && !overview) {
    return (
      <div aria-busy className="flex min-w-0 flex-col gap-2">
        <SettingsGroup>
          <SettingsGroupRow>
            <div className="flex flex-col gap-3">
              <Skeleton className="h-5 w-36" />
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          </SettingsGroupRow>
        </SettingsGroup>
      </div>
    );
  }

  const hasReviews = !!overview && overview.totalReviews > 0;

  if (!hasReviews) {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <SettingsGroupLabel>{labels.title}</SettingsGroupLabel>
        <SettingsGroup>
          <SettingsGroupRow>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-fit px-2 text-xs"
              onClick={onOpenReputation}
            >
              {labels.openReputation}
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </SettingsGroupRow>
        </SettingsGroup>
        <SettingsGroupFooter>{labels.emptySynced}</SettingsGroupFooter>
      </div>
    );
  }

  const summarySr = formatLocationReviewSummary(
    {
      averageRating: overview.averageRating,
      totalReviews: overview.totalReviews,
    },
    labels.summary,
    locale,
  );
  const summaryAria = formatLocationReviewSummary(
    {
      averageRating: overview.averageRating,
      totalReviews: overview.totalReviews,
    },
    labels.summaryAria,
    locale,
  );
  const countLabel = labels.reviewsCount.replace(
    '{count}',
    overview.totalReviews.toLocaleString(locale),
  );

  const aiUnavailable =
    summaryError || summary?.status === 'unavailable' || false;
  const aiLoading =
    !aiUnavailable &&
    (summaryLoading || summary?.status === 'generating' || !summary);

  const aiLabels: AiSummaryCardLabels = {
    ...labels.aiSummary,
    unavailable: labels.noSummary || labels.aiSummary.unavailable,
  };

  return (
    <AiSummaryCard
      summary={summary}
      loading={aiLoading}
      unavailable={aiUnavailable}
      labels={aiLabels}
      locale={locale}
      onInbox={onOpenInbox}
      onOpen={onOpenReputation}
      openLabel={labels.openReputation}
      hideWhenUnavailable={false}
      ratingMeta={
        <RatingMetaLine
          averageRating={overview.averageRating}
          summaryAria={summaryAria}
          summarySr={summarySr}
          countLabel={countLabel}
        />
      }
    />
  );
}
